import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedOutlined from '@mui/icons-material/RadioButtonUncheckedOutlined';
import Accordion, { AccordionProps } from '@mui/material/Accordion';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Card, { CardProps } from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Link, { LinkProps } from '@mui/material/Link';
import { SxProps, Theme } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { t } from 'i18next';
import { ElementType, ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { settingsPagePath } from '~router/routePaths';

const mergeSx = (baseSx: SxProps<Theme>, sx?: SxProps<Theme>) => [
  baseSx,
  ...(Array.isArray(sx) ? sx : [sx]),
];

export const onboardingLinkSx = (theme: Theme) => ({
  color: theme.semantic.linkText,
  textDecorationColor: theme.semantic.linkText,
});

export const completionToggleButtonProps = (isCompleted: boolean) =>
  isCompleted
    ? ({ color: 'secondary', sx: { flexShrink: 0 }, variant: 'outlined' } as const)
    : ({ color: 'brandV2Yellow', sx: { flexShrink: 0 }, variant: 'contained' } as const);

export const CompletionIcon = ({
  isCompleted,
  sx,
  ...props
}: SvgIconProps & { isCompleted: boolean }) => {
  const Icon = isCompleted ? CheckCircleOutlined : RadioButtonUncheckedOutlined;

  return (
    <Icon
      sx={mergeSx(
        (theme) => ({
          color: isCompleted
            ? theme.semantic.statusSuccess
            : theme.semantic.textSecondary,
        }),
        sx,
      )}
      {...props}
    />
  );
};

export const CompletionToggleButton = ({
  disabled,
  isCompleted,
  isIconOnly = false,
  onToggle,
  testId,
}: {
  disabled: boolean;
  isCompleted: boolean;
  isIconOnly?: boolean;
  onToggle: () => Promise<void>;
  testId?: string;
}) => {
  const [isToggling, setIsToggling] = useState(false);
  const label = isCompleted
    ? t('company_onboarding.actions.reopen')
    : t('company_onboarding.actions.mark_complete');

  const handleToggle = async () => {
    setIsToggling(true);

    try {
      await onToggle();
    } finally {
      setIsToggling(false);
    }
  };

  return isIconOnly ? (
    <IconButton
      aria-label={label}
      disabled={disabled || isToggling}
      onClick={handleToggle}
      sx={{ alignSelf: 'flex-start', p: 0 }}
    >
      <CompletionIcon isCompleted={isCompleted} />
    </IconButton>
  ) : (
    <SmallButton
      {...completionToggleButtonProps(isCompleted)}
      data-test-id={testId}
      disabled={disabled || isToggling}
      onClick={handleToggle}
    >
      {label}
    </SmallButton>
  );
};

export const ProgressQueryStatus = ({
  isError,
  isPending,
}: {
  isError: boolean;
  isPending: boolean;
}) => (
  <>
    {isError ? (
      <ShakyErrorIndicator
        message={t('error_messages.company_onboarding.failed_to_fetch')}
      />
    ) : null}
    {isPending ? <CommonLoader /> : null}
  </>
);

export const OnboardingCard = ({
  sx,
  ...props
}: CardProps & { component?: ElementType }) => (
  <Card
    variant="outlined"
    sx={mergeSx(
      (theme) => ({ borderColor: theme.semantic.borderStrong, borderRadius: 2 }),
      sx,
    )}
    {...props}
  />
);

export const OnboardingLink = ({ sx, ...props }: LinkProps & { to: string }) => (
  <Link
    component={RouterLink}
    sx={mergeSx(onboardingLinkSx, sx)}
    variant="body1"
    {...props}
  />
);

export const SettingsPageLink = ({
  settingsLink,
  sx,
  ...props
}: Omit<LinkProps, 'href'> & { settingsLink: string }) => (
  <OnboardingLink
    sx={mergeSx({ display: 'inline-block', mt: 1 }, sx)}
    to={settingsPagePath(settingsLink)}
    {...props}
  />
);

export const Eyebrow = ({ sx, ...props }: TypographyProps) => (
  <Typography
    sx={mergeSx({ color: 'text.secondary', letterSpacing: '0.08em' }, sx)}
    variant="subtitle2"
    {...props}
  />
);

export const SecondaryText = ({ sx, ...props }: TypographyProps) => (
  <Typography sx={mergeSx({ color: 'text.secondary' }, sx)} {...props} />
);

export const CaptionText = (props: TypographyProps) => (
  <SecondaryText variant="body2" {...props} />
);

export const TitleText = ({ sx, ...props }: TypographyProps) => (
  <Typography sx={mergeSx({ fontWeight: 600 }, sx)} {...props} />
);

export const LabeledLine = ({
  children,
  label,
  sx,
}: {
  children: ReactNode;
  label: string;
  sx?: SxProps<Theme>;
}) => (
  <Typography sx={sx}>
    <Box component="span" sx={{ fontWeight: 600 }}>
      {label}
    </Box>{' '}
    {children}
  </Typography>
);

export const StepCallout = ({
  body,
  children,
  severity = 'info',
  title,
}: {
  body: string;
  children?: ReactNode;
  severity?: 'info' | 'warning';
  title: string;
}) => (
  <Alert severity={severity} sx={{ my: 2 }}>
    <AlertTitle>{title}</AlertTitle>
    {body}
    {children}
  </Alert>
);

export const OnboardingExplainer = ({
  children,
  copyKey,
  titleKey = `${copyKey}.title`,
}: {
  children?: ReactNode;
  copyKey: string;
  titleKey?: string;
}) => (
  <>
    <TitleText>{t(titleKey)}</TitleText>
    <SecondaryText sx={{ mt: 0.5 }}>{t(`${copyKey}.what`)}</SecondaryText>
    <LabeledLine label={t('company_onboarding.use_when')} sx={{ mt: 0.5 }}>
      {t(`${copyKey}.when`)}
    </LabeledLine>
    {children}
  </>
);

export const OnboardingAccordion = ({ sx, ...props }: AccordionProps) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={mergeSx({ '&:before': { display: 'none' } }, sx)}
    {...props}
  />
);
